import { Component, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from 'angular-toastify';
import { Subject, takeUntil } from 'rxjs';
import { EventService } from 'src/app/core/services/event.service';
import { SettingsService } from 'src/app/core/services/settings.service';
import { UserService } from 'src/app/core/services/user.service';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnDestroy {
  email!: string;
  password!: string;
  form!: FormGroup;
  isLoading!: boolean;

  mlform!: FormGroup;

  // MFA
  mfaNeeded: boolean = false;
  mfaForm!: FormGroup;
  emailCodeForm!: FormGroup;
  magicLinkCodeForm!: FormGroup;
  magiclink!: string;
  factorId!: string;
  magicLinkCodeNeeded: boolean = false;
  SMSCodeNeeded: boolean = false;
  OTPSmsCodeForm!: FormGroup;

  // Email Code
  emailCodeNeeded: boolean = false;

  // Phone input (ngx-mat-intl-tel-input via ngModel)
  phoneValue: string = '';
  preferredCountries = ['br', 'us', 'gb'];

  // Phone formatted in E.164 — stored after sendSMSCode to reuse in submitSMSCode / resend
  authPhone: string = '';

  // Shown when the phone belongs to multiple users — email disambiguates which one
  phoneAmbiguous: boolean = false;
  smsEmailValue: string = '';

  // Countdown SMS
  countdown = 300;
  private countdownEndTime: number = 0;
  private intervalCountdownNumber: any;
  private visibilityChangeHandler = () => {
    if (document.visibilityState === 'visible' && this.countdownEndTime > 0) {
      this.countdown = Math.max(0, Math.round((this.countdownEndTime - Date.now()) / 1000));
      if (this.countdown <= 0) clearInterval(this.intervalCountdownNumber);
    }
  };

  destroySubject = new Subject();

  constructor(
    private fb: FormBuilder,
    private userService: UserService,
    private router: Router,
    private toastService: ToastService,
    private es: EventService,
    public settingsService: SettingsService,
    private translateService: TranslateService
  ) {}

  async ngOnInit() {
    // Captura síncrona dos params de auto-disparo vindos da landing page
    // (usuário existente redirecionado do cadastro para o login).
    const initialParams = this.router.routerState.snapshot.root.queryParams;
    const autoSend =
      initialParams['autosend'] === '1' || initialParams['autosend'] === 'true';

    // Discard any leftover postLoginRedirect from an unrelated prior session
    // before applying this visit's own redirect (or none). Must run
    // unconditionally — including the autosend path (LP → account_exists ->
    // /auth/login?autosend=1), which never carries a `redirect` param and
    // otherwise skips this cleanup entirely, letting a stale target from a
    // dropped session in a different case survive into this login (CPK-3909).
    localStorage.removeItem('postLoginRedirect');
    if (initialParams['redirect']) {
      localStorage.setItem('postLoginRedirect', initialParams['redirect']);
    }

    const usr = await !this.userService.getCurrentUser();
    if (!usr) {
      // Quando há auto-disparo, não navegamos para /login para preservar os
      // query params e o estado do componente (tela de código).
      if (!autoSend) {
        this.router.routerState.root.queryParams
          .pipe(takeUntil(this.destroySubject))
          .subscribe((params) => {
            console.log('login -> RouteParams', params);
            // Redirect to login page
            this.router.navigate(['/login']);
          });
      }
    } else {
      this.router.routerState.root.queryParams
        .pipe(takeUntil(this.destroySubject))
        .subscribe((params) => {
          console.log('login -> RouteParams', params);
          const redirectPath = params['redirect'];
          localStorage.removeItem('postLoginRedirect');
          this.router.navigateByUrl(redirectPath);
        });
    }

    this.form = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      magiclink: ['', [Validators.email]],
      password: ['', [Validators.required]],
      rememberMe: [false],
    });

    console.log('login -> form', this.form.getRawValue());

    this.mfaForm = this.fb.group({
      code: [
        '',
        [Validators.required, Validators.minLength(6), Validators.maxLength(6)],
      ],
    });

    this.emailCodeForm = this.fb.group({
      emailCode: [
        '',
        [Validators.required, Validators.minLength(6), Validators.maxLength(6)],
      ],
    });

    this.magicLinkCodeForm = this.fb.group({
      magicLinkCode: [
        '',
        [Validators.required, Validators.minLength(6), Validators.maxLength(6)],
      ],
    });

    this.OTPSmsCodeForm = this.fb.group({
      smsCode: [
        '',
        [Validators.required, Validators.minLength(6), Validators.maxLength(6)],
      ],
    });

    // Auto-disparo do código quando o usuário chega do cadastro (conta já
    // existente). A LP redireciona para /auth/login com o canal + autosend=1.
    if (autoSend) {
      this.handleAutoSend(initialParams);
    }
  }

  /**
   * Dispara automaticamente o fluxo de login por código (magic link / SMS)
   * para usuários que já possuem conta e foram redirecionados do cadastro.
   */
  private handleAutoSend(params: { [key: string]: any }): void {
    const email = params['magiclink'] || params['email'];
    const phone = params['phone'];

    if (email) {
      this.form.get('magiclink')?.setValue(email);
      this.sendMagicLink();
    } else if (phone) {
      this.phoneValue = phone;
      this.authPhone = phone;
      this.sendSMSCode();
    }
  }

  ngOnDestroy(): void {
    this.destroySubject.next(null);
    this.destroySubject.complete();
    clearInterval(this.intervalCountdownNumber);
    document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
  }

  async onSubmit() {
    this.isLoading = true;
    try {
      if (!this.form.valid) {
        this.toastService.error(
          'Please fill in the email and password correctly.'
        );
        return;
      }

      const email = this.form.get('email')?.value;
      this.email = email;
      const password = this.form.get('password')?.value;
      const rememberMe = this.form.get('rememberMe')?.value;
      this.password = password;
      const { data, error } = await this.userService.signIn(
        email,
        password,
        rememberMe
      );

      console.log('login -> AuthError', error?.message);
      if (
        error &&
        error.message &&
        error.message == 'Invalid login credentials'
      ) {
        throw new Error('Invalid login credentials');
      }

      if (error && error.message == 'Email not confirmed') {
        this.emailCodeNeeded = true;
      }
      const { session } = data;
      // Check if this user needed mfa code
      console.log(session);
      console.log(session?.user?.factors);

      const mfaNeeded =
        session?.user?.factors &&
        session?.user?.factors.filter((e) => e.status === 'verified').length >
          0;
      // if yes
      if (mfaNeeded) {
        // Hide login form and show MFA form
        if (!session) throw Error('No session found');
        if (!session.user) throw Error('No user found');
        if (!session.user.factors) throw Error('No factors found');
        if (!session.user.factors[0]) throw Error('No factor found');

        this.mfaNeeded = true;
        this.factorId = session.user.factors[0].id;
      } else {
        // Redirect to client page
        await this.enterApp();
      }

      if (error) {
        throw Error(error.message);
      }
    } catch (error: any) {
      this.toastService.error(error.message);
    } finally {
      this.isLoading = false;
    }
  }

  async sendMagicLink() {
    const email = this.form.get('magiclink')?.value;
    this.magiclink = email;
    try {
      this.isLoading = true;
      const { error } = await this.userService.sendMagicLinkEmail(email);
      if (error) throw error;
      this.magicLinkCodeNeeded = true;
    } catch (error: any) {
      if (error?.status === 422 && /signups not allowed/i.test(error?.message ?? '')) {
        this.toastService.error(this.translateService.instant('MagicLinkAccountNotFound'));
      } else {
        this.toastService.error(
          this.translateService.instant(error.message || 'Error')
        );
      }
    } finally {
      this.isLoading = false;
    }
  }

  async submitMfa() {
    try {
      this.isLoading = true;

      if (!this.mfaForm.valid) {
        throw Error('Invalid MFA code');
      }

      const code = this.mfaForm.get('code')?.value;
      if (!this.factorId) throw Error('No factorId found');
      if (!code) throw Error('No code found');

      const data = await this.userService.verifyMFACode(this.factorId, code);

      console.log('login -> Verify MFA Data: ', data);

      await this.enterApp();

      this.onSubmit();
    } catch (error: any) {
      this.toastService.error(error.message || error.msg || 'Error');
    } finally {
      this.isLoading = false;
    }
  }

  async submitCode() {
    try {
      this.isLoading = true;

      if (!this.emailCodeForm.valid) {
        throw Error('Invalid MFA code');
      }

      const code = this.emailCodeForm.get('emailCode')?.value;

      console.log('login -> code', code);

      const data = await this.userService.verifyEmail(this.email, code);

      console.log('login -> Verify MFA Data: ', data);

      await this.enterApp();
    } catch (error: any) {
      this.toastService.error(error.message || error.msg || 'Error');
    } finally {
      this.isLoading = false;
    }
  }

  async submitMagicLink() {
    try {
      this.isLoading = true;

      if (!this.magicLinkCodeForm.valid) {
        throw Error('Invalid Magic Link code');
      }

      const code = this.magicLinkCodeForm.get('magicLinkCode')?.value;

      console.log('login -> code', code);

      const { data, error } = await this.userService.logInWithOTP(
        this.magiclink,
        code
      );
      if (error) {
        throw { message: 'Token has expired or is invalid' };
      }

      console.log('login -> Verify MFA Data: ', data);

      await this.enterApp();
    } catch (error: any) {
      this.toastService.error(
        this.translateService.instant(error.message || error.msg || 'Error')
      );
    } finally {
      this.isLoading = false;
    }
  }

  stepBackMagicLink() {
    this.magicLinkCodeNeeded = false;
    this.magicLinkCodeForm.reset();
    this.form.get('magiclink')?.reset();
  }

  async sendSMSCode() {
    try {
      this.isLoading = true;
      await this.userService.sendSmsOtp(
        this.authPhone || this.phoneValue,
        this.phoneAmbiguous ? this.smsEmailValue : undefined
      );
      this.authPhone = this.phoneValue;
      this.SMSCodeNeeded = true;
      this.startCountdown();
    } catch (err: any) {
      const status = err?.status ?? err?.error?.status;
      if (status === 409) {
        this.phoneAmbiguous = true;
        this.toastService.info(this.translateService.instant('PhoneAmbiguousDesc'));
      } else if (status === 404) {
        this.toastService.error(this.translateService.instant('SMSLoginAccountNotFound'));
      } else {
        this.toastService.error(this.translateService.instant('Error'));
      }
    } finally {
      this.isLoading = false;
    }
  }

  async submitSMSCode() {
    try {
      this.isLoading = true;
      if (!this.OTPSmsCodeForm.valid) throw new Error('InvalidCode');
      const token = this.OTPSmsCodeForm.get('smsCode')?.value;
      const { error } = await this.userService.verifySmsOtp(this.authPhone, token);
      if (error) throw { message: 'Token has expired or is invalid' };
      await this.enterApp();
    } catch (error: any) {
      this.toastService.error(
        this.translateService.instant(error.message || 'Error')
      );
    } finally {
      this.isLoading = false;
    }
  }

  stepSMSVerificationCodeLink() {
    this.SMSCodeNeeded = false;
    this.OTPSmsCodeForm.reset();
    this.phoneValue = '';
    this.authPhone = '';
    this.phoneAmbiguous = false;
    this.smsEmailValue = '';
  }

  startCountdown() {
    this.countdownEndTime = Date.now() + 300 * 1000;
    this.countdown = 300;
    clearInterval(this.intervalCountdownNumber);
    document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
    document.addEventListener('visibilitychange', this.visibilityChangeHandler);
    this.intervalCountdownNumber = setInterval(() => {
      this.countdown = Math.max(0, Math.round((this.countdownEndTime - Date.now()) / 1000));
      if (this.countdown <= 0) clearInterval(this.intervalCountdownNumber);
    }, 500);
  }

  get countdownDisplay(): string {
    if (this.countdown <= 0) return '';
    const minutes = Math.floor(this.countdown / 60);
    const seconds = this.countdown % 60;
    const minLabel = this.translateService.instant(minutes === 1 ? 'Minute' : 'Minutes');
    const secLabel = this.translateService.instant(seconds === 1 ? 'Second' : 'Seconds');
    const andLabel = this.translateService.instant('And');
    if (minutes > 0 && seconds > 0) return `${minutes} ${minLabel} ${andLabel} ${seconds} ${secLabel}`;
    if (minutes > 0) return `${minutes} ${minLabel}`;
    return `${seconds} ${secLabel}`;
  }

  async enterApp() {
    const redirectPath = localStorage.getItem('postLoginRedirect');
    console.log('login -> enter app', { redirectPath });

    try {
      const user = await this.userService.getCurrentUserRole();
      const role: string | undefined = user?.primary_role;

      if (role === 'AGENT' && user?.attributes?.agent_partner === true) {
        await this.userService.signOut();
        this.toastService.error('Este perfil deve acessar apenas o Agent Portal.');
        return;
      }

      // Only call the verification lambda for roles that go through agent restrictions.
      // The lambda also has an internal safeguard for these roles as defense-in-depth.
      if (role && role !== 'CLIENT' && role !== 'ADMINISTRATOR' && role !== 'FUNDER') {
        let verifyResult: { verified: boolean; requiresSupervisor: boolean };
        try {
          verifyResult = await this.userService.verifyAgentPostLogin();
        } catch (err: any) {
          // Lambda returned 4xx — agent is inactive, out of schedule, or IP-blocked
          const message = err?.error?.message || err?.message || 'Access denied';
          await this.userService.signOut();
          this.toastService.error(message);
          return;
        }

        if (verifyResult.requiresSupervisor) {
          // Supervisor credentials required — redirect to dedicated agent login page
          console.log('login -> supervisor required, redirecting to agent login');
          this.router.navigateByUrl('/auth/login/agent');
          return;
        }
      }
    } catch (err) {
      console.warn('login -> enterApp verification failed, proceeding anyway', err);
    }

    // Navigate to the redirecting page which will handle the final destination
    // We do NOT remove postLoginRedirect here, as the RedirectingComponent needs it
    this.router.navigateByUrl('/client/redirecting');
  }
}
